/**
 * Team Inbox — `teamInboxAdmin` callable wrappers (impure seam, Phase 2).
 *
 * ONE action-routed callable serves the whole ownership surface (meta poll
 * · events · assign · claim · availability · settings upsert · device
 * registration) — see functions/teaminbox_admin.js. Lazy-imports firebase
 * exactly like lib/messagesStore.ts so every pure module above this seam
 * stays SDK-free for the Node test runner.
 */
import type {
  CannedReply,
  ClaimResult,
  InboxMeta,
  NumberRegistry,
  ThreadEvent,
} from './types';
import { normaliseRegistry } from './numbers';

async function call<TReq, TRes>(data: TReq): Promise<TRes> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('../../../lib/firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<TReq, TRes>(functions, 'teamInboxAdmin');
  const res = await fn(data);
  return res.data;
}

/** Ownership/claim map + me/teammates/registry/canned/escalations. */
export async function fetchInboxMeta(keys: string[]): Promise<InboxMeta> {
  const raw = await call<
    { action: 'meta'; keys: string[] },
    Omit<InboxMeta, 'registry'> & { registry: unknown }
  >({ action: 'meta', keys });
  // Server already normalises; re-normalise defensively so the UI shape
  // is guaranteed even across version skew.
  const registry: NumberRegistry = normaliseRegistry(raw.registry);
  if (
    raw.registry &&
    typeof raw.registry === 'object' &&
    (raw.registry as { fromFallback?: unknown }).fromFallback === true
  ) {
    registry.fromFallback = true;
  }
  return { ...raw, registry };
}

/** §7.3 system lines of ONE conversation. */
export async function fetchThreadEvents(
  conversationKey: string,
): Promise<ThreadEvent[]> {
  const res = await call<
    { action: 'events'; conversationKey: string },
    { ok: boolean; events: ThreadEvent[] }
  >({ action: 'events', conversationKey });
  return res.events || [];
}

/** Assign / reassign (staff: self-only — server-enforced). */
export async function assignConversation(
  conversationKey: string,
  toStaffId: string,
  note?: string,
): Promise<{ ok: boolean; ownerUid: string; ownerName: string }> {
  return call({
    action: 'assign' as const,
    conversationKey,
    toStaffId,
    note: note || '',
  });
}

/** Soft claim-lock heartbeat (focused) / release (blurred). */
export async function claimConversation(
  conversationKey: string,
  focused: boolean,
): Promise<ClaimResult> {
  return call({ action: 'claim' as const, conversationKey, focused });
}

/** My own Available/Away toggle. */
export async function setAvailability(
  available: boolean,
): Promise<{ ok: boolean; available: boolean }> {
  return call({ action: 'availability' as const, available });
}

/** S6 admin write — settings/wa_numbers. */
export async function upsertRegistry(value: unknown): Promise<void> {
  await call({ action: 'settingsUpsert' as const, doc: 'wa_numbers', value });
}

/** S6 admin write — settings/wa_canned. */
export async function upsertCanned(items: CannedReply[]): Promise<void> {
  await call({
    action: 'settingsUpsert' as const,
    doc: 'wa_canned',
    value: { items },
  });
}

/** Device-token registration (schema now; FCM consumes it Phase 3). */
export async function registerDevice(
  token: string,
  platform: 'web' | 'ios' | 'android',
): Promise<void> {
  await call({ action: 'registerDevice' as const, token, platform });
}

/* ── M3.3 classification callables (named v2 functions, not the dispatcher) ── */

async function namedCall<TReq, TRes>(name: string, data: TReq): Promise<TRes> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('../../../lib/firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<TReq, TRes>(functions, name);
  const res = await fn(data);
  return res.data;
}

/** Confirm or dismiss an AI classification proposal on one inbound message. */
export async function confirmClassification(params: {
  messageId: string;
  projectId: string;
  nodeType?: string;
  projectIds?: string[];
  taskTitle?: string | null;
  dismiss?: boolean;
}): Promise<{ ok: boolean; nodeType?: string; projectIds?: string[]; dismissed?: boolean }> {
  return namedCall('confirmMessageClassification', params);
}

/** Create a Type-2 chat task from a confirmed inbound message. */
export async function createChatTask(params: {
  messageId: string;
  projectId: string;
  contactId?: string;
  title: string;
}): Promise<{ ok: boolean; taskId: string }> {
  return namedCall('createTaskFromMessage', params);
}
